import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { isNativeShell } from '../../utils/distribution';
import {
  checkBiometricAvailability,
  isBiometricEnablePromptDismissed,
  isBiometricUnlockEnabled,
  markBiometricSessionUnlocked,
  setBiometricEnablePromptDismissed,
  setBiometricUnlockEnabled,
  verifyBiometricUnlock,
} from '../../utils/biometricUnlock';

/**
 * One-time offer after login in the native shell. Does not require TOTP.
 */
const BiometricEnablePrompt: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isNativeShell() || !isAuthenticated) {
      setOpen(false);
      return;
    }
    if (isBiometricUnlockEnabled() || isBiometricEnablePromptDismissed()) {
      if (isBiometricUnlockEnabled()) {
        markBiometricSessionUnlocked();
      }
      setOpen(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      const avail = await checkBiometricAvailability();
      if (!cancelled && avail.available) {
        setOpen(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleEnable = async () => {
    setBusy(true);
    setError(null);
    try {
      const ok = await verifyBiometricUnlock();
      if (!ok) {
        setError('Could not verify biometrics. You can try again from Account Settings.');
        return;
      }
      setBiometricUnlockEnabled(true);
      setBiometricEnablePromptDismissed(true);
      markBiometricSessionUnlocked();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleNotNow = () => {
    setBiometricEnablePromptDismissed(true);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleNotNow} maxWidth="xs" fullWidth>
      <DialogTitle>Use fingerprint or face unlock?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Unlock Qixit Mail quickly on this device after you sign in. You will still need your password
          (and authenticator code if enabled) after logout or when your session expires.
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleNotNow} disabled={busy}>
          Not now
        </Button>
        <Button variant="contained" onClick={() => void handleEnable()} disabled={busy}>
          {busy ? 'Checking…' : 'Enable'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BiometricEnablePrompt;
